package com.banyan.flightviewer.web.api

import com.banyan.flightviewer.api.Fr24Client
import com.banyan.flightviewer.platform.createHttpClient
import com.banyan.flightviewer.state.AircraftData
import com.banyan.flightviewer.state.AirportFlight
import com.banyan.flightviewer.state.AirportInfo
import com.banyan.flightviewer.state.DetectedLocation
import com.banyan.flightviewer.state.EnrichedFlightData
import com.banyan.flightviewer.state.FlightTrackPoint
import com.banyan.flightviewer.state.MetarData
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.parameter
import io.ktor.client.statement.HttpResponse
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json

/**
 * Browser-side client that calls the Ktor :server proxy endpoints. All
 * requests are same-origin (or proxied during dev via webpack-dev-server)
 * so no CORS surprises and no upstream API keys leak to the browser.
 *
 * Mirrors the methods on the underlying shared API clients but without
 * needing tokens -- the server holds those.
 */
class ServerClient {

    private val client = createHttpClient {
        install(ContentNegotiation) {
            json(Json { ignoreUnknownKeys = true })
        }
    }

    suspend fun getAircraft(
        lat: Double,
        lon: Double,
        radiusKm: Double,
        source: String = "adsbx",
    ): List<AircraftData> = try {
        client.get("/api/aircraft") {
            parameter("lat", lat)
            parameter("lon", lon)
            parameter("radiusKm", radiusKm)
            parameter("source", source)
        }.bodyOrEmpty()
    } catch (e: Throwable) {
        console.warn("getAircraft failed: ${e.message}")
        emptyList()
    }

    /**
     * Fetch a single aircraft's current position by its ICAO24 hex,
     * regardless of the user's monitoring radius. Used by the deep-link
     * share flow so a recipient far from the plane can still be taken
     * straight to it.
     *
     * Returns null if the aircraft isn't currently broadcasting OR the
     * server hasn't got ADSBX credentials configured. Callers treat null
     * the same in either case: fall back to waiting for the hex to show
     * up naturally through the monitoring loop.
     */
    suspend fun getAircraftByHex(hex: String): AircraftData? = try {
        val resp = client.get("/api/aircraft/by-hex/$hex")
        if (resp.status.value == 200) resp.body<AircraftData>() else null
    } catch (_: Throwable) {
        null
    }

    suspend fun bulkEnrich(callsigns: List<String>): Fr24Client.BulkEnrichResult? = try {
        if (callsigns.isEmpty()) null
        else client.get("/api/fr24/enrich") {
            parameter("callsigns", callsigns.joinToString(","))
        }.body<Fr24Client.BulkEnrichResult>()
    } catch (e: Throwable) {
        console.warn("bulkEnrich failed: ${e.message}")
        null
    }

    suspend fun getFlightDetails(callsign: String): EnrichedFlightData? = try {
        client.get("/api/fr24/details") { parameter("callsign", callsign) }
            .body<EnrichedFlightData>()
    } catch (_: Throwable) { null }

    suspend fun getFlightTracks(flightId: String, limit: Int = 300): List<FlightTrackPoint> = try {
        client.get("/api/fr24/tracks") {
            parameter("flightId", flightId)
            parameter("limit", limit)
        }.body<List<FlightTrackPoint>>()
    } catch (_: Throwable) { emptyList() }

    suspend fun getAirportInfo(code: String): AirportInfo? = try {
        client.get("/api/fr24/airport/$code/info").body<AirportInfo>()
    } catch (_: Throwable) { null }

    suspend fun getAirportDepartures(code: String, limit: Int = 50): List<AirportFlight> = try {
        client.get("/api/fr24/airport/$code/departures") { parameter("limit", limit) }
            .body<List<AirportFlight>>()
    } catch (_: Throwable) { emptyList() }

    suspend fun getAirportArrivals(code: String, limit: Int = 50): List<AirportFlight> = try {
        client.get("/api/fr24/airport/$code/arrivals") { parameter("limit", limit) }
            .body<List<AirportFlight>>()
    } catch (_: Throwable) { emptyList() }

    suspend fun getRecentLandings(code: String, limit: Int = 30): List<AirportFlight> = try {
        client.get("/api/fr24/airport/$code/landings") { parameter("limit", limit) }
            .body<List<AirportFlight>>()
    } catch (_: Throwable) { emptyList() }

    suspend fun getMetar(code: String): MetarData? = try {
        client.get("/api/weather/metar/$code").body<MetarData>()
    } catch (_: Throwable) { null }

    suspend fun getGeolocation(): DetectedLocation? = try {
        client.get("/api/geolocation").body<DetectedLocation>()
    } catch (_: Throwable) { null }

    /** Returns the URL the browser should set as `<audio src=...>`. */
    fun atcStreamUrl(mount: String): String = "/api/atc/stream?mount=$mount"

    /** Path to the photo proxy. The browser handles caching via standard HTTP. */
    fun photoUrl(hex: String, registration: String? = null): String {
        val regParam = registration?.takeIf { it.isNotBlank() }?.let { "?reg=$it" } ?: ""
        return "/api/photo/$hex$regParam"
    }

    /** Fetch raw photo bytes (used for inline data URLs in DetailPanel). */
    suspend fun fetchPhotoBytes(hex: String, registration: String?): ByteArray? = try {
        val resp = client.get(photoUrl(hex, registration))
        if (resp.status.value == 200) resp.body<ByteArray>() else null
    } catch (_: Throwable) {
        null
    }

    /**
     * Look up the URL of an airport's photo from the server, which pulls it
     * from Wikipedia. Returns null if no photo is found.
     */
    suspend fun getAirportPhotoUrl(code: String): String? = try {
        val resp = client.get("/api/airport/$code/photo")
        if (resp.status.value == 200) {
            val json = resp.body<kotlinx.serialization.json.JsonObject>()
            json["url"]?.let { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
        } else null
    } catch (_: Throwable) {
        null
    }

    private suspend inline fun <reified T> HttpResponse.bodyOrEmpty(): List<T> = try {
        body<List<T>>()
    } catch (_: Throwable) {
        emptyList()
    }
}
